import { createContext, useContext, useEffect, useState } from "react";
import type { AxiosResponse } from "axios";
import type { ApiError } from "@/types";
import type { EzyVetTelemetry } from "@/types/ezyvet";

export const DEFAULT_LIMIT = 60; // ezyVet: calls per minute per clinic database

export interface QuotaReading {
  remaining: number;
  limit: number;
  resetAt: number | null; // ms epoch
  observedAt: number; // ms epoch
  source: "headers" | "telemetry";
}

export interface EzyVetQuotaState {
  /** Latest known call budget, from whichever source reported most recently. */
  quota: QuotaReading | null;
  /** False when the backend has no telemetry endpoint and no headers have been seen. */
  quotaAvailable: boolean;
  quotaError: ApiError | null;
  connected: boolean;
  tokenExpiresAt: number | null; // ms epoch
  refresh: () => void;
}

export const EzyVetQuotaContext = createContext<EzyVetQuotaState | null>(null);

export function useEzyVetQuota(): EzyVetQuotaState {
  const ctx = useContext(EzyVetQuotaContext);
  if (!ctx) throw new Error("useEzyVetQuota must be used inside <EzyVetQuotaProvider>");
  return ctx;
}

/** Re-renders every `ms` while mounted; for countdowns. */
export function useNow(ms = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), ms);
    return () => window.clearInterval(id);
  }, [ms]);
  return now;
}

function header(res: AxiosResponse, name: string): number | null {
  const raw = res.headers?.[name];
  const n = raw === undefined || raw === null || raw === "" ? NaN : Number(raw);
  return Number.isFinite(n) ? n : null;
}

/** x-ratelimit-reset may be seconds-until-reset or an epoch timestamp (as in the backend client). */
export function resetToEpochMs(reset: number | null, now: number): number | null {
  if (reset === null) return null;
  return reset > 1_000_000_000 ? reset * 1000 : now + reset * 1000;
}

export function readingFromHeaders(res: AxiosResponse, now = Date.now()): QuotaReading | null {
  const remaining = header(res, "x-ratelimit-remaining");
  if (remaining === null) return null;
  return {
    remaining,
    limit: header(res, "x-ratelimit-limit") ?? DEFAULT_LIMIT,
    resetAt: resetToEpochMs(header(res, "x-ratelimit-reset"), now),
    observedAt: now,
    source: "headers",
  };
}

export function readingFromTelemetry(t: EzyVetTelemetry, observedAt: number): QuotaReading {
  return {
    remaining: t.rate_limit_remaining,
    limit: t.rate_limit_limit ?? DEFAULT_LIMIT,
    resetAt: t.rate_limit_reset_seconds != null ? observedAt + t.rate_limit_reset_seconds * 1000 : null,
    observedAt,
    source: "telemetry",
  };
}
